'use client'

/**
 * DividendCalculator: free tool for "dividend calculator / DRIP calculator / dividend reinvestment". Projects a
 * dividend portfolio over time with optional reinvestment (DRIP), dividend growth, price appreciation, and
 * monthly contributions. Shows the DRIP edge (reinvested vs not), final value, total dividends, and the passive
 * income + yield-on-cost at the end. Honest: it is an illustration, not advice. Pure client math (annual steps).
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const usd = (n: number): string => (isFinite(n) ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '$0')
const usdc = (n: number): string => (isFinite(n) ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '$0')

function project(opts: { start: number; yieldPct: number; divGrowth: number; priceGrowth: number; years: number; monthly: number; reinvest: boolean }) {
  const { start, yieldPct, divGrowth, priceGrowth, years, monthly, reinvest } = opts
  let price = 100 // arbitrary starting price; only ratios matter
  let shares = start > 0 ? start / price : 0
  let divPerShare = price * (yieldPct / 100)
  let totalDiv = 0
  let contributed = start
  for (let y = 0; y < years; y++) {
    const annualDiv = shares * divPerShare
    totalDiv += annualDiv
    if (reinvest && price > 0) shares += annualDiv / price
    // monthly contributions buy shares through the year (approximate at year-avg price)
    if (monthly > 0 && price > 0) { shares += (monthly * 12) / price; contributed += monthly * 12 }
    // grow price + dividend for next year
    price *= 1 + priceGrowth / 100
    divPerShare *= 1 + divGrowth / 100
  }
  const value = shares * price
  const finalIncome = shares * divPerShare
  return { value, totalDiv, finalIncome, shares, contributed }
}

export default function DividendCalculator() {
  const [start, setStart] = useState('10000')
  const [yieldPct, setYieldPct] = useState('4')
  const [divGrowth, setDivGrowth] = useState('5')
  const [priceGrowth, setPriceGrowth] = useState('5')
  const [years, setYears] = useState('20')
  const [monthly, setMonthly] = useState('0')
  const [reinvest, setReinvest] = useState(true)

  const r = useMemo(() => {
    const o = {
      start: parseFloat(start) || 0, yieldPct: parseFloat(yieldPct) || 0, divGrowth: parseFloat(divGrowth) || 0,
      priceGrowth: parseFloat(priceGrowth) || 0, years: Math.max(0, Math.min(60, Math.floor(parseFloat(years) || 0))), monthly: parseFloat(monthly) || 0,
    }
    const drip = project({ ...o, reinvest: true })
    const noDrip = project({ ...o, reinvest: false })
    const chosen = reinvest ? drip : noDrip
    const yoc = o.start > 0 ? (chosen.finalIncome / o.start) * 100 : 0
    const edge = drip.value - noDrip.value
    const invalid = o.start <= 0 || o.years <= 0
    return { ...chosen, yoc, edge, dripValue: drip.value, noDripValue: noDrip.value, invalid }
  }, [start, yieldPct, divGrowth, priceGrowth, years, monthly, reinvest])

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <CalcField label="Starting investment" value={start} onChange={setStart} prefix="$" step="500" hint="What you invest today" />
        <CalcField label="Dividend yield" value={yieldPct} onChange={setYieldPct} suffix="%" step="0.1" hint="Annual, at today's price" />
        <CalcField label="Dividend growth" value={divGrowth} onChange={setDivGrowth} suffix="%" step="0.5" hint="How fast the payout grows / yr" />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <CalcField label="Share price growth" value={priceGrowth} onChange={setPriceGrowth} suffix="%" step="0.5" hint="Annual price appreciation" />
        <CalcField label="Years" value={years} onChange={setYears} step="1" hint="Time horizon" />
        <CalcField label="Monthly contribution" value={monthly} onChange={setMonthly} prefix="$" step="50" hint="Optional, added each month" />
      </div>
      <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-zinc-300">
        <input type="checkbox" checked={reinvest} onChange={(e) => setReinvest(e.target.checked)} className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 accent-emerald-500" />
        Reinvest dividends (DRIP)
      </label>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter a starting investment and a time horizon to project your dividend portfolio.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border px-5 py-4" style={{ borderColor: '#34d3994d', background: '#34d39912' }}>
              <div className="text-[12px] font-bold uppercase tracking-wider text-emerald-300">Portfolio value</div>
              <div className="mt-1 text-[32px] font-black leading-none tabular-nums text-white">{usdc(r.value)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">after {years} years{reinvest ? ' with DRIP' : ' (no reinvestment)'}</div>
            </div>
            <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">Annual income at year {years}</div>
              <div className="mt-1 text-[32px] font-black leading-none tabular-nums text-emerald-400">{usdc(r.finalIncome)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">{r.yoc.toFixed(1)}% yield on your original cost</div>
            </div>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Total dividends collected</div>
              <div className="mt-0.5 text-[18px] font-bold tabular-nums text-zinc-100">{usdc(r.totalDiv)}</div>
            </div>
            <div className="rounded-xl border px-4 py-3" style={{ borderColor: '#34d39933', background: '#34d3990d' }}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-300">The DRIP advantage</div>
              <div className="mt-0.5 text-[18px] font-bold tabular-nums text-emerald-400">+{usdc(r.edge)}</div>
              <div className="text-[11px] text-zinc-500">reinvesting vs taking the cash ({usd(r.dripValue)} vs {usd(r.noDripValue)})</div>
            </div>
          </div>

          <p className="mt-3 text-[11px] leading-snug text-zinc-500">
            An illustration that compounds your inputs each year. Real dividends can be cut, prices fall, and taxes apply, so actual results vary. Not investment advice.
          </p>
        </>
      )}
    </div>
  )
}
